import { createFileRoute } from "@tanstack/react-router";
import { Breadcrumbs, PageHeader, Section, SectionHeading } from "@/components/site/section";
import { CtaLink } from "@/components/site/cta";
import { FinalCTA } from "@/components/site/final-cta";
import { pageUrl } from "@/data/site";

export const Route = createFileRoute("/tube-on-clear-cast-blanks")({
  head: () => ({
    meta: [
      { title: "Tube-On Clear-Cast Pen Blanks | Paul's Pens n Things" },
      {
        name: "description",
        content:
          "Clear-cast braided carbon fiber, stainless steel, copper and denim blanks cast directly on Sierra and Jr Series tubes. $14.95 each, tiered shipping.",
      },
      { property: "og:title", content: "Tube-on clear-cast pen blanks" },
      {
        property: "og:description",
        content:
          "Braided sleeves and denim cast in clear resin straight onto Sierra and Jr Series pen tubes — no drilling, nothing shifts.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: pageUrl("/tube-on-clear-cast-blanks") },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: pageUrl("/tube-on-clear-cast-blanks") }],
  }),
  component: TubeOnClearCastBlanks,
});

type Blank = {
  name: string;
  price: string;
  image: string;
  alt: string;
  spec: string;
};

const sierra: Blank[] = [
  {
    name: "Carbon fiber Sierra",
    price: "$14.95",
    image: "/tube-on-carbon-fiber-sierra.webp",
    alt: "Clear-cast braided carbon fiber sleeve on a Sierra pen tube",
    spec: "Clear cast braided carbon fiber on a Sierra tube.",
  },
  {
    name: "Braided stainless steel Sierra",
    price: "$14.95",
    image: "/tube-on-braided-stainless-steel-sierra.webp",
    alt: "Clear-cast braided stainless steel sleeve on a Sierra pen tube",
    spec: "Clear cast braided stainless steel on a Sierra tube.",
  },
  {
    name: "Braided copper Sierra",
    price: "$14.95",
    image: "/tube-on-braided-copper-sierra.webp",
    alt: "Clear-cast braided copper sleeve on a Sierra pen tube",
    spec: "Clear cast braided copper on a Sierra tube.",
  },
  {
    name: "Denim Sierra",
    price: "$14.95",
    image: "/tube-on-denim-sierra.webp",
    alt: "Clear-cast denim cloth wrapped on a Sierra pen tube",
    spec: "Clear cast denim cloth on a Sierra tube.",
  },
];

const jrSeries: Blank[] = [
  {
    name: "Carbon fiber Jr Series",
    price: "$14.95",
    image: "/tube-on-carbon-fiber-jr-series.webp",
    alt: "Clear-cast carbon fiber on Jr Series pen tubes",
    spec: "Clear cast carbon fiber on Jr Series tubes.",
  },
  {
    name: "Braided copper Jr Series",
    price: "$14.95",
    image: "/tube-on-braided-copper-jr-series.webp",
    alt: "Clear-cast braided copper on Jr Series pen tubes",
    spec: "Clear cast braided copper on Jr Series tubes.",
  },
  {
    name: "Braided stainless steel Jr Series",
    price: "$14.95",
    image: "/tube-on-braided-stainless-steel-jr-series.webp",
    alt: "Clear-cast braided stainless steel on Jr Series pen tubes",
    spec: "Clear cast braided stainless steel on Jr Series tubes.",
  },
];

function BlankGrid({ items }: { items: Blank[] }) {
  return (
    <ul className="mt-12 grid gap-px border border-hairline bg-hairline sm:grid-cols-2 lg:grid-cols-4">
      {items.map((b) => (
        <li key={b.name} className="flex flex-col bg-background">
          <div className="overflow-hidden bg-paper">
            <img
              src={b.image}
              alt={b.alt}
              loading="lazy"
              width={360}
              height={480}
              className="h-full w-full object-cover motion-craft transition-transform duration-700 hover:scale-[1.03]"
            />
          </div>
          <div className="flex flex-1 flex-col p-7 sm:p-8">
            <h3 className="font-[family-name:var(--font-display)] text-2xl leading-tight">{b.name}</h3>
            <p className="mt-2 text-lg font-semibold text-walnut">{b.price}</p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{b.spec}</p>
            <div className="mt-auto pt-6">
              <CtaLink to="/contact" variant="outline" className="h-11">
                Order this blank
              </CtaLink>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function TubeOnClearCastBlanks() {
  return (
    <>
      <Breadcrumbs
        trail={[
          { label: "Home", to: "/" },
          { label: "For Penturners", to: "/for-pen-turners" },
          { label: "Tube-On Clear-Cast Blanks" },
        ]}
      />
      <PageHeader
        eyebrow="Products for penturners"
        title="Tube-on clear-cast blanks."
        lede="Clear cast braided sleeves and denim on Sierra tubes, and clear cast braided sleeves on Jr Series tubes. Cast straight onto the tube, so nothing shifts and there is no drilling to do."
      >
        <CtaLink to="/contact" variant="outline" className="h-12">
          Ask about availability
        </CtaLink>
      </PageHeader>

      <Section tone="paper" labelledBy="sierra-heading">
        <div className="shell">
          <SectionHeading
            id="sierra-heading"
            eyebrow="Sierra tubes"
            title="Braided sleeves and denim on Sierra tubes."
            lede="Each blank is $14.95. Shipping is $6 for 1 to 5, $11 for 6 to 25, and $19 for 26 or more."
          />
          <BlankGrid items={sierra} />
        </div>
      </Section>

      <Section labelledBy="jr-heading">
        <div className="shell">
          <SectionHeading
            id="jr-heading"
            eyebrow="Jr Series tubes"
            title="Braided sleeves on Jr Series tubes."
            lede="Each blank is $14.95. Shipping is $6 for 1 to 5, $11 for 6 to 25, and $19 for 26 or more."
          />
          <BlankGrid items={jrSeries} />
          <p className="mt-8 max-w-2xl text-sm text-muted-foreground">
            Turning a batch for a club or a class? Tell Paul how many you need and he will quote it with the right
            shipping tier applied.
          </p>
        </div>
      </Section>

      <FinalCTA
        heading="Want a sleeve or fabric cast on your own tubes?"
        body="Send Paul your kit and material and he will cast the blanks to match."
        label="Start a conversation"
      />
    </>
  );
}
